<script setup lang="ts">
import type { Challenge, ChallengeGoal } from '~/types/challenge'

const { t } = useI18n()

const props = defineProps<{
  challenge: Challenge
}>()

const hasDistanceGoal = computed(() => {
  return props.challenge.goals.distance && props.challenge.goals.distance.length > 0
})

const hasGeoGoal = computed(() => {
  return props.challenge.goals.geo && props.challenge.goals.geo.length > 0
})

const hasTripSharingGoal = computed(() => {
  return props.challenge.goals.trip_sharing && props.challenge.goals.trip_sharing.length > 0
})

const longestDistanceGoal = computed(() => {
  if (!props.challenge.goals.distance) return null

  return props.challenge.goals.distance.reduce<ChallengeGoal>(
    (acc, goal) => {
      return goal.target > acc.target ? goal : acc
    },
    { target: 0, progress: 0, image_url: null }
  )
})
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-6">
    <div v-if="hasDistanceGoal && longestDistanceGoal">
      <h3 class="tw-mb-2 tw-font-bold">{{ t('Distance') }}</h3>

      <div class="tw-flex tw-flex-col tw-gap-2">
        <div class="tw-relative tw-mt-4">
          <ChartsProgress
            :progress="
              longestDistanceGoal.progress
                ? longestDistanceGoal.progress / longestDistanceGoal.target
                : 0
            "
          />

          <div
            v-for="goal in challenge.goals.distance"
            :key="goal.target"
            class="tw-absolute tw-top-0 tw-z-10 tw-h-full tw-w-8 -tw-translate-x-1/2"
            :style="{
              left: `min(${(goal.target / longestDistanceGoal.target) * 100}%, calc(100% - 25px))`
            }"
          >
            <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
              <FontAwesomeIcon icon="star" class="tw-text-lg tw-text-riser-blue/30" />
            </div>

            <div
              class="tw-absolute tw-left-1/2 tw-top-0 -tw-translate-x-1/2 -tw-translate-y-full tw-text-center"
            >
              <UnitItem
                :value="Math.round(goal.target / 1000)"
                current-unit="km"
                class="tw-text-sm"
              >
                <template #value="{ value }">
                  <span class="tw-font-bold">{{ value }}</span>
                </template>

                <template #unit="{ unit }">&nbsp;{{ unit }}</template>
              </UnitItem>

              <div
                v-if="goal.progress && goal.progress >= goal.target"
                class="tw-absolute -tw-top-0.5 tw-right-0 tw-flex tw-h-3 tw-w-3 tw-translate-x-full tw-items-center tw-justify-center tw-rounded-full tw-bg-riser-green tw-text-white"
                style="font-size: 0.5rem"
              >
                <FontAwesomeIcon icon="check" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasGeoGoal">
      <h3 class="tw-mb-2 tw-font-bold">{{ t('Locations') }}</h3>

      <div
        class="tw-grid tw-gap-4"
        style="grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr))"
      >
        <ChallengeGoalProgressCircleItem
          v-for="(goal, index) in props.challenge.goals.geo"
          :key="index"
          :goal="goal"
          fallback-icon="location-dot"
        />
      </div>
    </div>

    <div v-if="hasTripSharingGoal">
      <h3 class="tw-mb-2 tw-font-bold">{{ t('Trip Sharing') }}</h3>

      <div class="tw-flex tw-flex-col tw-gap-2">
        <div
          v-for="(goal, index) in props.challenge.goals.trip_sharing"
          :key="index"
          class="tw-flex tw-justify-between tw-gap-4"
        >
          <p>{{ t('Share-trip') }}</p>

          <p v-if="goal.progress && goal.progress >= goal.target" class="tw-text-riser-green">
            {{ t('Done') }}
          </p>

          <p v-else>{{ goal.progress }} / {{ goal.target }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
